// frontend/src/utils/SessionManager.js
export const SessionManager = {
  loadSessions() {
    try {
      const raw = localStorage.getItem('sessions');
      return raw ? JSON.parse(raw) : { sessions: [], activeUserId: null };
    } catch (err) {
      console.error('Error parsing sessions:', err);
      return { sessions: [], activeUserId: null };
    }
  },

  saveSessions(sessionsObject) {
    localStorage.setItem('sessions', JSON.stringify(sessionsObject));
  },

  addSession(userId, role, token, name, icon_path) {
    const data = this.loadSessions();
    // 同じ userId のセッションが既に存在するなら更新
    const existing = data.sessions.find(s => s.userId === userId);
    if (existing) {
      existing.token = token;
      existing.role = role;
      existing.name = name || existing.name;
      existing.icon_path = icon_path || existing.icon_path;
    } else {
      data.sessions.push({ 
        userId, 
        role, 
        token,
        name,
        icon_path
      });
    }
    // 新たなログインで activeUserId を更新する
    data.activeUserId = userId;
    this.saveSessions(data);
    
    // ログイン成功イベントを発火
    window.dispatchEvent(new CustomEvent('login'));
  },

  removeSession(userId) {
    const data = this.loadSessions();
    data.sessions = data.sessions.filter(s => s.userId !== userId);
    if (data.activeUserId === userId) {
      data.activeUserId = null;
    }
    this.saveSessions(data);
  },

  setActiveUser(userId) {
    const data = this.loadSessions();
    const found = data.sessions.find(s => s.userId === userId);
    if (found) {
      data.activeUserId = userId;
      this.saveSessions(data);
    } else {
      console.warn('No such session to set active:', userId);
    }
  },

  getActiveSession() {
    const data = this.loadSessions();
    if (!data.activeUserId) return null;
    return data.sessions.find(s => s.userId === data.activeUserId) || null;
  },

  /**
   * 現在アクティブなセッションからトークンを取得
   * @returns {string|null} トークン、またはnull
   */
  getToken() {
    const session = this.getActiveSession();
    return session ? session.token : null;
  },

  getAllSessions() {
    return this.loadSessions().sessions;
  },

  /**
   * 現在のアクティブなユーザー情報を取得する
   * @returns {Object|null} ユーザー情報オブジェクト、またはnull
   */
  getUser() {
    const session = this.getActiveSession();
    if (!session) return null;
    
    return {
      id: session.userId,
      role: session.role,
      name: session.name,
      icon_path: session.icon_path
    };
  },

  /**
   * ログアウト時に、localStorage からセッション情報をクリアするメソッド
   * セッション情報をクリアし、ログアウトイベントを発火します
   */
  logout() {
    // セッション情報を削除
    localStorage.removeItem('sessions');
    
    // ログアウト専用のイベントを発火
    // セッション切れのunauthorizedイベントとは区別して処理
    window.dispatchEvent(new CustomEvent('logout'));
  },

  /**
   * セッション情報をクリアする
   * ログアウトイベントを発火しない簡易版のクリア処理
   */
  clearSession() {
    localStorage.removeItem('sessions');
  },

  /**
   * アクティブセッションのトークンのみを更新する（パスワード変更後のトークン再発行に使用）
   * @param {string} newToken 新しいトークン
   */
  updateToken(newToken) {
    const data = this.loadSessions();
    const session = data.sessions.find(s => s.userId === data.activeUserId);
    if (session) {
      session.token = newToken;
      this.saveSessions(data);
    }
  },

  /**
   * must_change_password フラグを設定する（管理者リセット後のログイン時）
   * @param {boolean} value
   */
  setMustChangePassword(value) {
    const data = this.loadSessions();
    const session = data.sessions.find(s => s.userId === data.activeUserId);
    if (session) {
      session.must_change_password = value;
      this.saveSessions(data);
    }
  },

  /**
   * must_change_password フラグを取得する
   * @returns {boolean}
   */
  getMustChangePassword() {
    const session = this.getActiveSession();
    return session ? !!session.must_change_password : false;
  }
};
